<script lang='ts'>
  import { createEventDispatcher, onMount } from 'svelte'
  import { _, date } from 'svelte-i18n'
  import { getTypedAm5, type am5DateAxisWithAxisRenderer, type am5DataItemWithIDateAxisDataItem, type am5Tooltip, type am5CircleTemplate, type am5Root } from '../../Util/XertAm5';
  import { addWeeks, endOfWeek, format, startOfWeek } from 'date-fns';
  import { specificityToRating, uniqueId } from '@/Util/utils';
  import { getFocus, xssColors, CONSTRAINT_EPSILON } from '@/Common/constants';

  const { am5, am5xy, am5themes_Animated } = getTypedAm5()
  am5.addLicense('AM5C87942260');
  
  export let athletes = {}
  export let data = []
  export let range = null
  /** Dates ('yyyy-MM-dd') drawn in the darker activity colours, to point out the days an adapted forecast changes. */
  export let changedDates: string[] = []
  /**
   * Draws the axes the way the Progression tab's other charts do (small grey labels, no axis lines,
   * faint gridlines) and lets the caller set the height. Left off, the chart is unchanged for its
   * other users.
   */
  export let compact = false
  export let height = '500px'

  // TODO: Use uniqueId for chart id as well.
  export let id = 'progression_chart';

  let legendElement: HTMLElement
  let legendId = uniqueId('chart_legend_')

  const dispatch = createEventDispatcher();

  //@ts-ignore
  $: window.debugData = data // TODO: Remove

  let dateAxis: am5DateAxisWithAxisRenderer
  let tlSeries = null, formSeries

  let ltlSeries, htlSeries, ptlSeries
  // let lrlSeries, hrlSeries, prlSeries

  let xssStrokeSeries, xssSeries, sbXssSeries

  let weeklyHoursSeries, focusPowerSeries

  let todayRange: am5DataItemWithIDateAxisDataItem
  let highlightRange: am5DataItemWithIDateAxisDataItem


  const colours = {
    // Original progression chart colours
    // low: am5.color('#f59b9b'),
    // high: am5.color('#6188ff'),
    // peak: am5.color('#76ff00'),

    // Colours to match MPA chart
    low: am5.color(xssColors.xlss),
    high: am5.color(xssColors.xhss),
    peak: am5.color(xssColors.xpss),

    // Freshness colours.
    very_fresh: am5.color('#00FF00'),
    fresh: am5.color('#0000FF'),
    tired: am5.color('#FFC200'),
    very_tired: am5.color('#FF0000'),
    detraining: am5.color('#B45F04'),

    black: am5.color('#000000'),
    white: am5.color('#ffffff'),
  }
  // light colors for column chart without opacity.
  const lighter_colours = {
    // Colours to match MPA chart
    low: am5.Color.lighten(am5.color(xssColors.xlss), 0.4),
    high: am5.Color.lighten(am5.color(xssColors.xhss), 0.4),
    peak: am5.Color.lighten(am5.color(xssColors.xpss), 0.4),
  }


  // Fill in chart background to show start/end of week
  let weekRanges = []
  $: if (tlSeries && data.length) {
    let _data = [];
    let _xssData = [];
    let _weeklyHoursData = [];
    const changedSet = new Set(changedDates)

    // Split each day into two data points on chart, before and after the activity.
    let tlData = [];
    for(const {after, xlss, xhss, xpss, weekly_hours, ...before} of data) {
      // Convert seconds to milliseconds.
      const t = before.t * 1000
      _data.push({...before, t})
      _weeklyHoursData.push({t, weekly_hours})

      if (before.tls) {
        tlData.push({
          t,
          ltl: before.tls.l, htl: before.tls.h, ptl: before.tls.p,
          lrl: before.rls.l, hrl: before.rls.h, prl: before.rls.p,
          lf: before.tls.l - before.rls.l,
          hf: before.tls.h - before.rls.h,
          pf: before.tls.p - before.rls.p,
          ltl_target: before.ltl_target, htl_target: before.htl_target, ptl_target: before.ptl_target,
          lrl_target: before.lrl_target, hrl_target: before.hrl_target, prl_target: before.prl_target,
          lf_target: before.ltl_target - before.lrl_target,
          hf_target: before.htl_target - before.hrl_target,
          pf_target: before.ptl_target - before.prl_target,
        })
      }

      // Split xlss/xhss/xpss so it only appears in separate xss data set for stacked column
      _xssData.push({
        ...before,
        t,
        changed: changedSet.size > 0 && changedSet.has(format(new Date(t), 'yyyy-MM-dd')),
        xss_tooltip: Math.max(50.0, before.xss), // Min value for xss here to ensure invisible xssSeries has a hitbox to show tooltips for rest days
        xlss, xhss, xpss,
        // xssColumn: {
        //   opacity: before.event ? 1.0 : 0.6
        // },
      })
      if (after && after.tls) {
        _data.push({...before, ...after, t})
        tlData.push({
          t,
          ltl: after.tls.l, htl: after.tls.h, ptl: after.tls.p,
          lrl: after.rls.l, hrl: after.rls.h, prl: after.rls.p,
          lf: after.tls.l - after.rls.l,
          hf: after.tls.h - after.rls.h,
          pf: after.tls.p - after.rls.p,
          ltl_target: before.ltl_target, htl_target: before.htl_target, ptl_target: before.ptl_target,
          lrl_target: before.lrl_target, hrl_target: before.hrl_target, prl_target: before.prl_target,
          lf_target: before.ltl_target - before.lrl_target,
          hf_target: before.htl_target - before.hrl_target,
          pf_target: before.ptl_target - before.prl_target,
        })
      }
    }
    _data = _data.map(p=>{
      if (p.status) {
        let stroke = colours[p.status]
        p.rlStroke = {
          stroke,
          fill: stroke,
        }
      }
      return p
    })
    tlSeries.data.setAll(_data)

    // TODO: Maybe generate these colours in webassembly?
    formSeries.data.setAll(_data)
    // tlSeries._dataSets[dataSetId] = this._mainDataItems;

    ltlSeries.data.setAll(tlData)
    htlSeries.data.setAll(tlData)
    ptlSeries.data.setAll(tlData)
    // lrlSeries.data.setAll(tlData)
    // hrlSeries.data.setAll(tlData)
    // prlSeries.data.setAll(tlData)

    weeklyHoursSeries.data.setAll(_weeklyHoursData)
    focusPowerSeries.data.setAll(_data)

    xssSeries.data.setAll(_xssData)
    xssStrokeSeries.data.setAll(_xssData)
    sbXssSeries.data.setAll(_xssData)
    // scoreSeries.data.setAll(data)

    todayRange.setAll({
      value: _data[0].t - 7*24*3600*1000,
      endValue: new Date().getTime(),
    })

    for (let range of weekRanges) {
      dateAxis.axisRanges.removeValue(range)
    }

    // {weekStartsOn: 1} Use Monday to Sunday as the week definition for more consistent weekly hours calculation.
    let day = startOfWeek(new Date(_data[0].t), {weekStartsOn: 1})
    let end = endOfWeek(new Date(_data[_data.length-1].t), {weekStartsOn: 1})
    while(day < end) {
      const range = dateAxis.createAxisRange(dateAxis.makeDataItem({
        value: day.getTime(),
        endValue: endOfWeek(day).getTime()
      }))

      range.get('axisFill').setAll({
        fill: colours.black,
        fillOpacity: 0.05,
        visible: true
      });
      weekRanges.push(range)
      day = addWeeks(day, 2)
    }

    // todayRangeDataItem.set('value', _data[0].t - 24*3600*1000)
    // todayRangeDataItem.set('endValue', new Date().getTime())
  }

  $: if (data && highlightRange) {
    if (range && data[range[0]] && data[range[1]])
      highlightRange.setAll({
        value: data[range[0]].t*1000,
        endValue: data[range[1]].t*1000,
      })
    else {
      highlightRange.setAll({
        value: 0,
        endValue: 0,
      })

    }
  }

  onMount(()=>{
    let root = am5.Root.new(id)

    // Use local timezone for date axis
    root.utc = false
    // TODO: Maybe better to use utc with $activity->start_date_locale instead?
    // root.utc = true
    // root.timezone = am5.Timezone.new('America/Toronto')

    let legendRoot = am5.Root.new(legendId)
    root.setThemes([am5themes_Animated.new(root)])
    legendRoot.setThemes([am5xy.DefaultTheme.new(legendRoot)])

    let chart = root.container.children.push( am5xy.XYChart.new(root, {
      paddingTop: -8,
      paddingLeft: 0,
      // paddingBottom: 0,
      // paddingRight: 0,

      //pinchZoomX: true,
      //panX: true,
      //wheelY: "zoomX",
      layout: root.verticalLayout,
      // arrangeTooltips: false
    }) )

    let axisConfig = {
      strokeOpacity: 1,
      strokeWidth: 2,
      minGridDistance: 40,
    }
    let axisGridConfig = {
      disabled: true,
      visible: false,
    }

    let xssAxisRenderer = am5xy.AxisRendererY.new(root, {
      ...axisConfig,
        visible: false,
        forceHidden: true,
    })
    xssAxisRenderer.labels.template.set('visible', false)
    xssAxisRenderer.grid.template.setAll({
        ...axisGridConfig,
    })

    let xssAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      maxPrecision: 1,
      min: 0,
      // max: 300,
      // max: 350,
      // strictMinMaxSelection: true,
      // strictMinMax: true,
      extraMax: 0.05,
      // includeGuidesInMinMax: false,
      renderer: xssAxisRenderer,
      // visible: true,
      // tooltip: am5.Tooltip.new(root, {
      //     themeTags: ['axis']
      // })
    }))
    // tlAxis.ghostLabel.set('minWidth', 52)

    let formAxisRenderer = am5xy.AxisRendererY.new(root, {
      ...axisConfig,
      opposite: true,
      stroke: colours.tired
    })
    formAxisRenderer.grid.template.setAll(axisGridConfig)
    let formAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      maxPrecision: 1,
      renderer: formAxisRenderer,
      visible: false,
      forceHidden: true,
      // tooltip: am5.Tooltip.new(root, {
      //     themeTags: ['axis']
      // })
    }))

    let tlAxisRenderer = am5xy.AxisRendererY.new(root, {
      ...axisConfig,
      opposite: true,
      // visible: false,
      // forceHidden: true,
    })
    tlAxisRenderer.grid.template.setAll(axisGridConfig)
    let tlAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      // maxPrecision: 1,
      min: 0,
      extraMax: 0.1,
      renderer: tlAxisRenderer,
      // tooltip: am5.Tooltip.new(root, {
      // 		themeTags: ['axis']
      // })
      // visible: false,
      // forceHidden: true,
    }))

    let hoursAxisRenderer = am5xy.AxisRendererY.new(root, {
      ...axisConfig,
      opposite: true,
      visible: false,
      forceHidden: true,
    })
    hoursAxisRenderer.labels.template.set('visible', false)
    hoursAxisRenderer.grid.template.setAll(axisGridConfig)
    let hoursAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      // maxPrecision: 1,
      min: 0,
      extraMax: 0.1,
      renderer: hoursAxisRenderer,
      // visible: false,
      // forceHidden: true,
    }))

    let powerAxisRenderer = am5xy.AxisRendererY.new(root, {
      ...axisConfig,
      opposite: true,
    })
    powerAxisRenderer.grid.template.setAll(axisGridConfig)
    let powerAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      // maxPrecision: 1,
      // min: 0,
      extraMax: 0.1,
      renderer: powerAxisRenderer,
    }))

    let dateAxisRenderer = am5xy.AxisRendererX.new(root, {
      strokeOpacity: 1,
      strokeWidth: 2,
    })
    dateAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
      extraMax: 0.02,

      baseInterval: {timeUnit: 'raw', count: 24*3600*1000},

      // tooltipIntervalOffset should match locationX: 0 of series otherwise it shows value 12 hours in future.
      snapTooltip: false,
      tooltipIntervalOffset: 0.0,

      renderer: dateAxisRenderer,
      // tooltipDateFormat: 'ss',
      tooltipDateFormat: 'EEE, dd MMM yyyy',
      tooltip: am5.Tooltip.new(root, {
          themeTags: ['axis'],
      })
    }))
    dateAxis.adapters.add('min', (min)=>{
      // Offset min by -6 hours to account for locationX: 0 used on all series. Otherwise columns get cut off on the first day
      // since default behaviour is to have column in between adjacent grid lines, while this chart has the columns centred on the grid.
      // Since column is centered, only half of the total space is needed, so -6 hours compared to the 12 hour default offset.
      return min - 6*3600*1000
    })
    dateAxisRenderer.grid.template.setAll(axisGridConfig)
    dateAxisRenderer.labels.template.setAll({
      minPosition: 0.05,
      location: 0.0,
      multiLocation: 0.0,
    })

    if (compact) {
      const narrow = (document.getElementById(id)?.clientWidth ?? 1000) < 640
      const labelStyle = (fill: string) => ({ fontSize: narrow ? 10 : 11, fill: am5.color(fill), paddingLeft: 3, paddingRight: 3 })
      tlAxisRenderer.labels.template.setAll(labelStyle('#1F2937'))
      powerAxisRenderer.labels.template.setAll(labelStyle('#B8860B'))
      formAxisRenderer.labels.template.setAll(labelStyle('#6B7280'))
      for (const renderer of [tlAxisRenderer, powerAxisRenderer, formAxisRenderer, dateAxisRenderer]) {
        renderer.setAll({ strokeOpacity: 0 })
      }
      // The load scale draws the gridlines; the date axis gets faint ones too.
      tlAxisRenderer.grid.template.setAll({ disabled: false, visible: true, strokeOpacity: 0.08 })
      dateAxisRenderer.grid.template.setAll({ disabled: false, visible: true, strokeOpacity: 0.08 })
      dateAxisRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') })
    }

    const tooltipBackground = {
      stroke: colours.black,
      strokeWidth: 1.5,
      strokeOpacity: 1,

      fill: colours.white,
      fillOpacity: 0.85,
    }
    const tooltipSettings = {
      getFillFromSprite: false,
      getStrokeFromSprite: false,
    }
    root._tooltip.setAll({
      ...tooltipSettings,
      autoTextColor: false
    })
    root._tooltip.get('background').setAll(tooltipBackground)

    const xssTooltip = am5.Tooltip.new(root, {
      // forceHidden: true,
      ...tooltipSettings,
      autoTextColor: false,
      pointerOrientation: 'horizontal'
    })
    xssStrokeSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
      valueXField: 't',
      valueYField: 'xss',
      // stacked: true,
      clustered: false,
      // name: '',
      yAxis: xssAxis,
      xAxis: dateAxis,

      locationX: 0.0,

      stroke: colours.white,

      // Fallback colour when no gradient applied in fillGradient adapter, used to show debug info like days with failed constrants
      fill: am5.color('#FF00FF'),

      // stroke: am5.color('#00FF00'),
      // fill: am5.color('#AAFF88')
    }))
    xssStrokeSeries.columns.template.setAll({
      // fillOpacity: 0.0,
      // fillOpacity: 0.0,
      strokeWidth: 1.0,
      strokeOpacity: 1.0,
      // location: 0.0,

      // width: am5.percent(100),
      // dy: -10,
    })
    xssStrokeSeries.columns.template.adapters.add('stroke', (stroke, target)=>{
      let val = target.dataItem?.dataContext
      if (val && (val.d_constraint > CONSTRAINT_EPSILON || val.intensity_unmet)) return am5.color('#FF00FF')
      return stroke
    })
    xssStrokeSeries.columns.template.adapters.add('fillGradient', (fillGradient, target)=>{
      let val = target.dataItem?.dataContext

      if (!(val?.xss > 0)) return fillGradient
      const peak = Math.max(0.0, val.xpss/val.xss);
      const high = Math.max(0.0, val.xpss + val.xhss)/val.xss;
      
      // Use lighter colours for placeholder activities.
      // This is done instead of opacity so that overlapping columns don't blend together
      const cs = (val?.changed || val?.state?.includes('event') || val?.state?.includes('scheduled') || val?.state?.includes('completed')) ? colours : lighter_colours

      // Gradient oriented top to bottom, so peak is at 0 and low at 1.0
      const stops = [
        { offset: 0.0, color: cs.peak },
        { offset: peak, color: cs.peak },
        { offset: peak, color: cs.high },
        { offset: high, color: cs.high },
        { offset: high, color: cs.low },
        { offset: 1.0, color: cs.low }
      ]
      if (!fillGradient)
        fillGradient = am5.LinearGradient.new(root, {
          stops,
          rotation: 90
        })
      else fillGradient.set('stops', stops)
      return fillGradient
  });
    // xssStrokeSeries.columns.template.adapters.add('height', (h: number, target)=>{
    //   return h + 10.0
    // })


    // Invisible xss columns as a target for tooltips.
    xssSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
      valueXField: 't',
      valueYField: 'xss_tooltip',
      // stacked: true,
      clustered: false,
      name: 'Xss',
      yAxis: xssAxis,
      xAxis: dateAxis,

      locationX: 0.0,

      fill: colours.low,
      // stroke: colours.white,
      tooltip: xssTooltip
    }))
    xssSeries.columns.template.setAll({
      // fillOpacity: 0.0,
      // strokeWidth: 2.0,
      // strokeOpacity: 1.0,

      opacity: 0.0,
      width: am5.percent(100)
    })

    // Handle clicking on bar
    xssSeries.columns.template.events.on('click', (event)=>{
      const val = event.target.dataItem?.dataContext
      if (val) {
        dispatch('dayclick', val)
      }
    })

    tlSeries = chart.series.push(am5xy.LineSeries.new(root, {
      name: 'Training Load',
      valueXField: 't',
      valueYField: 'tl',
      yAxis: tlAxis,
      xAxis: dateAxis,
      stroke: colours.black,
      locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
      tooltip: am5.Tooltip.new(root, {
        ...tooltipSettings,
        labelText: '{name}: [bold]{valueY.formatNumber("#.#")}[/] '+
        `[bold ${colours.low.toString()}]{tls.l.formatNumber("#.#")}[/] `+
        `[bold ${colours.high.toString()}]{tls.h.formatNumber("#.#")}[/] `+
        `[bold ${colours.peak.toString()}]{tls.p.formatNumber("#.#")}[/]\n`+
        'Signature: '+
        `[bold ${colours.low.toString()}]{ftp.formatNumber("#.")}[/] `+
        `[bold ${colours.high.toString()}]{hie_kj.formatNumber("#.#")}[/] `+
        `[bold ${colours.peak.toString()}]{pp.formatNumber("#.")}[/]`
      }),
    }))
    tlSeries.getTooltip().get('background').setAll(tooltipBackground)

    tlSeries.strokes.template.setAll({
      strokeWidth: 3.0,
      strokeOpacity: 0.6,
    })

    ltlSeries = chart.series.push(am5xy.LineSeries.new(root, {
      name: 'Low',
      valueXField: 't',
      valueYField: 'ltl',
      yAxis: tlAxis,
      xAxis: dateAxis,
      stroke: colours.low,
      locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
      tooltip: am5.Tooltip.new(root, {
        ...tooltipSettings,
        // labelText: `[black]ltl/lrl (form):[/] [bold ${colours.low.toString()}]{ltl.formatNumber("#.#")}/{lrl.formatNumber("#.#")} ({lf.formatNumber("#.#")})[/]\n`
        labelText: `[black]Low Load:[/] [bold ${colours.low.toString()}]{ltl.formatNumber("#.#")}[/]`
      }),
    }))
    ltlSeries.strokes.template.setAll({
      strokeWidth: 3.0,
      strokeOpacity: 0.6,
    })
    ltlSeries.getTooltip().get('background').setAll(tooltipBackground)
    // lrlSeries = chart.series.push(am5xy.LineSeries.new(root, {
    //   name: 'lrl',
    //   valueXField: 't',
    //   valueYField: 'lrl',
    //   yAxis: tlAxis,
    //   xAxis: dateAxis,
    //   stroke: lighter_colours.low,
    //   locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
    // }))
    // lrlSeries.strokes.template.setAll({
    //   strokeWidth: 2.0,
    //   strokeOpacity: 0.6,
    // })
    htlSeries = chart.series.push(am5xy.LineSeries.new(root, {
      name: 'High',
      valueXField: 't',
      valueYField: 'htl',
      yAxis: tlAxis,
      xAxis: dateAxis,
      stroke: colours.high,
      locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
      tooltip: am5.Tooltip.new(root, {
        ...tooltipSettings,
        // labelText: `[black]htl/hrl (form):[/] [bold ${colours.high.toString()}]{htl.formatNumber("#.#")}/{hrl.formatNumber("#.#")} ({hf.formatNumber("#.#")})[/]`
        labelText: `[black]High Load:[/] [bold ${colours.high.toString()}]{htl.formatNumber("#.#")}[/]`
      }),
    }))
    htlSeries.getTooltip().get('background').setAll(tooltipBackground)
    htlSeries.strokes.template.setAll({
      strokeWidth: 3.0,
      strokeOpacity: 0.6,
    })
    // hrlSeries = chart.series.push(am5xy.LineSeries.new(root, {
    //   name: 'hrl',
    //   valueXField: 't',
    //   valueYField: 'hrl',
    //   yAxis: tlAxis,
    //   xAxis: dateAxis,
    //   stroke: lighter_colours.high,
    //   locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
    // }))
    // hrlSeries.strokes.template.setAll({
    //   strokeWidth: 2.0,
    //   strokeOpacity: 0.6,
    // })
    ptlSeries = chart.series.push(am5xy.LineSeries.new(root, {
      name: 'Peak',
      valueXField: 't',
      valueYField: 'ptl',
      yAxis: tlAxis,
      xAxis: dateAxis,
      stroke: colours.peak,
      locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
      tooltip: am5.Tooltip.new(root, {
        ...tooltipSettings,
        //labelText: `[black]ptl/prl (form):[/] [bold ${colours.peak.toString()}]{ptl.formatNumber("#.#")}/{prl.formatNumber("#.#")} ({pf.formatNumber("#.#")})[/]`
        labelText: `[black]Peak Load:[/] [bold ${colours.peak.toString()}]{ptl.formatNumber("#.#")}[/]`
      }),
    }))
    ptlSeries.getTooltip().get('background').setAll(tooltipBackground)
    ptlSeries.strokes.template.setAll({
      strokeWidth: 3.0,
      strokeOpacity: 0.6,
    })
    // prlSeries = chart.series.push(am5xy.LineSeries.new(root, {
    //   name: 'prl',
    //   valueXField: 't',
    //   valueYField: 'prl',
    //   yAxis: tlAxis,
    //   xAxis: dateAxis,
    //   stroke: lighter_colours.peak,
    //   locationX: 0, // NOTE: All series on DateAxis need this set to 0 otherwise there will be a 12 hour offset in the data.
    // }))
    // prlSeries.strokes.template.setAll({
    //   strokeWidth: 2.0,
    //   strokeOpacity: 0.6,
    // })

    ltlSeries.on('visible', function(visible) {
      //if (visible) for (let ss of [tlSeries, htlSeries, hrlSeries, ptlSeries, prlSeries]) ss.hide();
      if (visible) for (let ss of [tlSeries, htlSeries, ptlSeries]) ss.hide();
      //if (visible) lrlSeries.show()
      //else lrlSeries.hide()
    })
    htlSeries.on('visible', function(visible) {
      // if (visible) for (let ss of [tlSeries, ltlSeries, lrlSeries, ptlSeries, prlSeries]) ss.hide();
      if (visible) for (let ss of [tlSeries, ltlSeries, ptlSeries]) ss.hide();
      // if (visible) hrlSeries.show()
      // else hrlSeries.hide()
    })
    ptlSeries.on('visible', function(visible) {
      // if (visible) for (let ss of [tlSeries, ltlSeries, lrlSeries, htlSeries, hrlSeries]) ss.hide();
      if (visible) for (let ss of [tlSeries, ltlSeries, htlSeries]) ss.hide();
      // if (visible) prlSeries.show()
      // else prlSeries.hide()
    })
    for (let s of [ltlSeries, htlSeries, ptlSeries]) s.hide();


    formSeries = chart.series.push(am5xy.LineSeries.new(root, {
      name: 'Form',
      valueXField: 't',
      valueYField: 'form',
      yAxis: formAxis,
      xAxis: dateAxis,
      locationX: 0,
      visible: false
    }))
    let bulletTemplate: am5CircleTemplate = am5.Template.new({
      fill: colours.fresh,
    })
    formSeries.bullets.push(function(root: am5Root, series, dataItem) {
      let tooltipFormat = '[#000000]Form: [bold]{valueY.formatNumber("#.#")}[/] '+
        `[bold ${colours.low.toString()}]{lf.formatNumber("#.#")}[/] `+
        `[bold ${colours.high.toString()}]{hf.formatNumber("#.#")}[/] `+
        `[bold ${colours.peak.toString()}]{pf.formatNumber("#.#")}[/]`
      let val = dataItem.dataContext
      if (val.t_rest_l != null) {
        tooltipFormat += '\n[#000000]Recovery (days): '+
        `[bold ${colours.low.toString()}]{t_rest_l.formatNumber("#.#")}[/] `+
        `[bold ${colours.high.toString()}]{t_rest_h.formatNumber("#.#")}[/] `+
        `[bold ${colours.peak.toString()}]{t_rest_p.formatNumber("#.#")}[/]`
      }
      // if (true) {
      //   tooltipFormat += '\n[#000000]DEBUG tls: '+
      //   `[bold ${colours.black.toString()}]{tl.formatNumber("#.#")}[/] `+
      //   `[bold ${colours.low.toString()}]{tls.l.formatNumber("#.#")}[/] `+
      //   `[bold ${colours.high.toString()}]{tls.h.formatNumber("#.#")}[/] `+
      //   `[bold ${colours.peak.toString()}]{tls.p.formatNumber("#.#")}[/]`
      //   tooltipFormat += '\n[#000000]DEBUG rls: '+
      //   `[bold ${colours.black.toString()}]{rl.formatNumber("#.#")}[/] `+
      //   `[bold ${colours.low.toString()}]{rls.l.formatNumber("#.#")}[/] `+
      //   `[bold ${colours.high.toString()}]{rls.h.formatNumber("#.#")}[/] `+
      //   `[bold ${colours.peak.toString()}]{rls.p.formatNumber("#.#")}[/]`
      // }
      const circle = am5.Circle.new(root, {
        radius: 5,
        templateField: 'rlStroke',
        tooltipText: tooltipFormat,
        strokeWidth: 20, // Large invisible stroke to increase the hover range of the tooltip.
        strokeOpacity: 0.0,
        // showTooltipOn: 'always',
        // tooltip,
      }, bulletTemplate)
      circle.events.on('pointerover', function() {
        debouncer.showFormTooltip()
      })
      circle.events.on('pointerout', function() {
        debouncer.showOtherTooltip()
      })
      return am5.Bullet.new(root, {
        locationY: 0.0,
        locationX: 0.0,
        sprite: circle
      })
    });
    formSeries.strokes.template.setAll({
        strokeWidth: 3.0,
        strokeOpacity: 0.6,
        templateField: 'rlStroke',
    })
    formSeries.events.on('datavalidated', function() {
      // const minmax = Math.max(-formAxis.getPrivate('min'), formAxis.getPrivate('max'))
      const minmax = Math.max(-formSeries.getPrivate('minY'), +formSeries.getPrivate('maxY'))*1.5
      formAxis.setAll({
        min: -minmax,
        max: minmax,
        start: 0,
        end: 1
      });
    });
    weeklyHoursSeries = chart.series.push(am5xy.LineSeries.new(root, {
      valueXField: 't',
      valueYField: 'weekly_hours',

      name: 'Weekly Hours',

      stroke: am5.color('#FF5B5B'),
      fill: colours.white, // Set fill to white so that label text is auto-set to black.

      yAxis: hoursAxis,
      xAxis: dateAxis,
      locationX: 0,

      tooltip: am5.Tooltip.new(root, {
        ...tooltipSettings,
        labelText: '{name}: [bold]{valueY.formatNumber("#.#")}[/]',
      }),
    }))
    weeklyHoursSeries.getTooltip().get('background').setAll(tooltipBackground)
    weeklyHoursSeries.strokes.template.setAll({
      strokeWidth: 4.0,
      opacity: 0.4,
    })

    focusPowerSeries = chart.series.push(am5xy.LineSeries.new(root, {
      valueXField: 't',
      // valueYField: 'fm', // Fitness Measure power
      valueYField: 'fp', // Pure focus power

      name: 'Focus Power',

      stroke: am5.color('#d88c00'),
      fill: colours.white, // Set fill to white so that label text is auto-set to black.

      yAxis: powerAxis,
      xAxis: dateAxis,
      locationX: 0,

      tooltip: am5.Tooltip.new(root, {
        ...tooltipSettings,
        labelText: '{name}: [bold]{valueY.formatNumber("#.#")} W[/]',
      }),
    }))
    focusPowerSeries.getTooltip().get('background').setAll(tooltipBackground)
    focusPowerSeries.strokes.template.setAll({
      strokeWidth: 4.0,
      opacity: 0.7,
      strokeDasharray: [10, 5, 2, 5]
    })

    const seriesWithTooltips = [tlSeries, focusPowerSeries, weeklyHoursSeries]

    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {
      behavior: 'zoomX',
      // snapToSeries: seriesWithTooltips,

      snapToSeries: [xssSeries],
      snapToSeriesBy: 'x',
    }))

    todayRange = dateAxis.createAxisRange(dateAxis.makeDataItem({
      value: new Date().getTime(),
      endValue: new Date().getTime(),
    }));

    todayRange.get('grid').setAll({
      // stroke: colours.low,
      // strokeOpacity: 1

      stroke: colours.black,
      strokeWidth: 2,
      // strokeOpacity: 0.5,
      strokeOpacity: 1.0,
      strokeDasharray: [3, 3],
      visible: true,
      
      // Line at end of range.
      location: 1.0,
    });

    // todayRange.get('axisFill').setAll({
    //   fill: am5.color(0x00FF00),
    //   fillOpacity: 0.2,
    //   visible: true
    // });

    todayRange.get('label').setAll({
      // fill: am5.color(0xFF0000),
      text: 'Today',
      visible: true,
      location: 1.0,

      inside: true,
      rotation: -90,
      dx: -20,
      // dy: 10,
      centerX: am5.p100,
    });

    highlightRange = dateAxis.createAxisRange(dateAxis.makeDataItem({
      value: new Date().getTime(),
      endValue: new Date().getTime(),
    }));

    highlightRange.get('axisFill').setAll({
      fill: am5.color(0x00FF00),
      fillOpacity: 0.2,
      visible: true
    });

    tlAxis.events.on('boundschanged', (event)=>{
        let h = tlAxis.height();
        if (h > 0) {
          todayRange.get('label').set('dy', 10 - h)
        }
    });

    // ;(rlSeries.get('tooltip').label as am5.Label).setAll({
    //     textAlign: 'center',
    // })

    // Debounce switching between different types of tooltip based on what the cursor is hovering over.
    enum TooltipsToShow {
      XssColumn,
      FormBullet,
      OtherSeries
    }
    let debouncer = {
      tooltipToShow: TooltipsToShow.OtherSeries,
      debounceTimer: null,
      showXssTooltip(){
        if (this.tooltipToShow != TooltipsToShow.XssColumn) {
          this.tooltipToShow = TooltipsToShow.XssColumn
          this.debounce()
        }
      },
      showFormTooltip(){
        if (this.tooltipToShow != TooltipsToShow.FormBullet) {
          this.tooltipToShow = TooltipsToShow.FormBullet
          this.debounce()
        }
      },
      showOtherTooltip(){
        if (this.tooltipToShow != TooltipsToShow.OtherSeries) {
          this.tooltipToShow = TooltipsToShow.OtherSeries
          this.debounce()
        }
      },
      debounce(){
        if (this.debounceTimer != null) clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(()=>{
          this.debounceTimer = null;
          switch(this.tooltipToShow) {
            case TooltipsToShow.XssColumn:
              for (let s of seriesWithTooltips) { s.getTooltip()?.set('forceHidden', true) }
              xssTooltip.set('forceHidden', false)
              break;
            case TooltipsToShow.FormBullet:
              for (let s of seriesWithTooltips) { s.getTooltip()?.set('forceHidden', true) }
              xssTooltip.set('forceHidden', true)
              break;
            case TooltipsToShow.OtherSeries:
              for (let s of seriesWithTooltips) { s.getTooltip()?.set('forceHidden', false) }
              xssTooltip.set('forceHidden', true)
              break;
          }
          // chart.arrangeTooltips()
        }, 200)
      }
    }

    xssSeries.columns.template.events.on('pointerover', function() {
      debouncer.showXssTooltip();
    })
    xssSeries.columns.template.events.on('pointerout', function() {
      debouncer.showOtherTooltip();
    })

    let scrollbar = am5xy.XYChartScrollbar.new(root, {
      orientation: 'horizontal',
      // height: 50
      height: 30,
    })
    chart.set('scrollbarX', scrollbar)

    let sbDateAxisRenderer = am5xy.AxisRendererX.new(root, {
        visible: false,
    });
    let sbDateAxis = scrollbar.chart.xAxes.push(am5xy.DateAxis.new(root, {
        // groupData: true,
        // groupCount: 900,

        renderer: sbDateAxisRenderer,
        baseInterval: {timeUnit: 'raw', count: 24*3600*1000},
        tooltipDateFormat: 'ss',
        tooltipIntervalOffset: 0.0,
    }))
    sbDateAxisRenderer.grid.template.setAll({
        disabled: true,
        visible: false,
    })
    sbDateAxisRenderer.labels.template.setAll({
        disabled: true,
        visible: false,
    })

    let sbXssAxisRenderer = am5xy.AxisRendererY.new(root, {
        ...axisConfig,
        visible: false,
    })
    sbXssAxisRenderer.grid.template.setAll(axisGridConfig);

    let sbXssAxis = scrollbar.chart.yAxes.push(am5xy.ValueAxis.new(root, {
      maxPrecision: 1,
      min: 0,
      extraMax: 0.05,
      // strictMinMax: true,
      renderer: sbXssAxisRenderer,
    }))

    sbXssSeries = scrollbar.chart.series.push(am5xy.ColumnSeries.new(root, {
        // connect: false,
        valueXField: 't',
        valueYField: 'xss',

        locationX: 0.0,
        clustered: false,
        // stroke: am5.color('#000000'),

        name: 'Xss',
        yAxis: sbXssAxis,
        xAxis: sbDateAxis,
    }))


    for(let series of [xssSeries]) {
      const tooltip = series.getTooltip() as am5Tooltip
      tooltip.label.setAll({
        textAlign: 'center',
      })

      tooltip.get('background').setAll(tooltipBackground)

      series.columns.template.setAll({
        tooltipX: am5.percent(100),
        // tooltipY: am5.percent(0),
        tooltipY: am5.percent(50),
        tooltipText: '{categoryX}: {valueY}'
      });
    }
    const today = new Date()
    xssTooltip.label.adapters.add('html', (text, target)=> {
      if (!(target && target.dataItem)) return null
      let val = target.dataItem.dataContext as any
      if (!val) return null
      const prev = val.i > 0 ? data[val.i-1] : val
      const xss = val.xss ?? prev.xss
      const xlss = val.xlss ?? prev.xlss
      const xpss = val.xpss ?? prev.xpss
      const xhss = val.xhss ?? prev.xhss
      const sp = val.sp ?? prev.sp
      const sfd = val.sfd ?? prev.sfd
      const adu = val.adu ?? prev.adu

      // Based on trainingStatus in helpers.php. TODO: Move to utils.ts?
      function numberOfStars(v) {
        let xs = [25, 50, 75, 110,  150];
        let ys = [1.0, 2.0, 3.0, 4.0, 5.0];
        // if (v <= 40) return 20;

        let y = 100;
        xs.every((x, i) => {
          if (x > v) {
            let t = (v-xs[i-1])/(x-xs[i-1]);
            y = (ys[i-1] + t*(ys[i]-ys[i-1])-0.25)*20;
            return false;
          }
          return true;
        });
        return Math.min(100, Math.max(20, y));
      }

      const state = val.state ?? []
      const isPlaceholder = state.includes('placeholder') ?? false

      text = '<div style="text-align: center;">'
      // Activity name
      // if (val.event && val.a_name)
      if (!isPlaceholder && val.a_name)
        text += `<strong>${val.a_name}</strong><br>`

      const activity_date = new Date(val.t)
      text += `<strong>${$date(activity_date, {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric'})}</strong><br>`
      if (val.lf != null && (isPlaceholder || activity_date > today))
        if (xpss > 0 || xhss > 0) {
          text += `<h4>High Intensity</h4>`
        } else {
          text += `<h4>Low Intensity</h4>`
        }
        if (colours[val.status]) {
          text += `<strong>Training Status:</strong><span class="fa am5-stars" style="--star-color: ${colours[val.status].toString()}; --percent: ${numberOfStars(val.tl)}%"></span><br>`
        }

      if (xss > 0) {
        const spr = specificityToRating(sp)
        
        if (xpss > 0 || xhss > 0) {
          text +=`<strong>XSS: ${xss.toFixed(0)}</strong> `+
          ` (<strong style="color: ${colours.low.toString()};">${xlss.toFixed(1)}</strong> | `+
          `<strong style="color: ${colours.high.toString()};">${xhss.toFixed(1)}</strong> | `+
          `<strong style="color: ${colours.peak.toString()};">${xpss.toFixed(1)}</strong>)`
          text += `<br><strong>${spr} ${getFocus(athletes, sfd)}</strong>`
        } else {
          text += `<strong>XSS:</strong> ${xss.toFixed(0)}<br><strong>Pure Endurance</strong>`
        }
      text += `<br>${(adu).toFixed(1)} hours`
      } else {
        // TODO: Use $_ svelte-i18n
        let type: string;
        if (state.includes('rest')) type = "Rest"
        else if (state.includes('recovery')) type = "Recovery"
        else type = ''
        text += `<strong>${type}</strong><br>`
      }
      
      if (val.notes) {
        // Constraint notes on a day whose d_constraint is only rounding noise would warn about something the chart doesn't draw as a conflict.
        const noise = val.d_constraint > 0 && val.d_constraint <= CONSTRAINT_EPSILON
        const notes = val.notes.split(', ').filter(note => note && !(noise && !/^(weekly_hours_exceeded|intensity_|event_)/.test(note)))
        if (notes.length > 0) {
          text += '<br><b>Warnings</b><br>'
          for (const note of notes) text += $_('forecast.constraints.'+note)+'<br>'
        }
      }

      text += '</div>'

      return text

    })

    let legend = legendRoot.container.children.push(am5.Legend.new(legendRoot, {
        width: am5.percent(100),
    }));
    legend.data.setAll([
      tlSeries,
      ltlSeries, htlSeries, ptlSeries,
      formSeries, weeklyHoursSeries, focusPowerSeries
    ])

    if (compact) {
      // Same small text as the legend chips on the other charts.
      legend.labels.template.setAll({ fontSize: 12 })
      chart.series.each((series) => series.getTooltip()?.label.setAll({ fontSize: 12 }))
      legend.itemContainers.template.setAll({ paddingTop: 2, paddingBottom: 2, paddingRight: 12 })
    }

    legend.events.on('boundschanged', function(){
      // console.log('AM5 legend bounds changed', (legend.height() + 6));
      if (legendElement) {
        legendElement.style.height = (legend.height() + 6) + 'px';
      }
    });
  })

</script>

<div class="progression-chart" id={id} style="height: {height}"></div>
<div bind:this={legendElement} id={legendId}></div>
<style lang='scss'>
:global {
  span.am5-stars {
    --star-color: black;
    --star-background: lightgray;

    &::before {
    // content: '◆ ◆ ◆ ◆ ◆';
    content: "\f005\f005\f005\f005\f005";
    // font-size: 2rem;
    background: linear-gradient(90deg, var(--star-color) var(--percent), var(--star-background) var(--percent));

    background-clip: text;
    -webkit-background-clip: text;

    -webkit-text-fill-color: transparent;
    }
  }
}
.progression-chart {
  height: 500px;
}
</style>
